import { queryOptions } from '@tanstack/react-query'
import type { AnnotationSelection } from '@/api/annotationModels'
import { getAnnotation, type AnnotationEnvelope } from '@/api/annotations'
import { API_BASE, ApiError, fetchJson, path } from '@/api/client'
import type { CharacterBody, Landmarks, SemanticMarker } from '@/schema/character'

export interface SpriteAsset { uid: string; name: string; kind: 'character'; thumb_url: string; ready: boolean; clips: number; actions: string[] }
export type SpriteListItem = SpriteAsset
export interface SpriteFilters { q?: string; ready?: boolean; limit?: number }
export interface SpriteSuggestions { uid: string; clip: string; base_rev: number; frame_landmarks: Record<string, Landmarks>; notes: string; is_mock: boolean; provider?: string | null; model?: string; source_id?: string; semantic_markers?: SemanticMarker[] }
export interface MarkerContext { source_id: string; base_rev: number; clip: string }
export function getMarkerContext(uid: string, body: { clip: string; base_rev: number; body: CharacterBody }, signal?: AbortSignal): Promise<MarkerContext> {
  return fetchJson(path`/api/sprites/${uid}/marker-context`, { method: 'POST', body, signal })
}

export function listSprites(filters: SpriteFilters = {}, signal?: AbortSignal): Promise<{ items: SpriteAsset[]; total: number }> {
  return fetchJson('/api/sprites', { query: { ...filters }, signal })
}
export const spritesQuery = (filters: SpriteFilters = {}) => queryOptions({ queryKey: ['sprites', filters], queryFn: ({ signal }) => listSprites(filters, signal) })

/** Exact ready cast lookup; unlike the catalog this is not limited to one page. */
export function getSprite(uid: string, signal?: AbortSignal): Promise<SpriteAsset> {
  return fetchJson(path`/api/sprites/${uid}`, { signal })
}

export async function getSpriteAnnotation(uid: string): Promise<AnnotationEnvelope<CharacterBody>> {
  try { return await getAnnotation(uid) as AnnotationEnvelope<CharacterBody> }
  catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error
    return fetchJson(path`/api/sprites/${uid}/prepare`, { method: 'POST' })
  }
}
export function suggestSprite(uid: string, body: { clip: string; frames: number[]; base_rev: number; parts: string[]; semantic_markers?: boolean } & AnnotationSelection): Promise<{ job_id: number }> {
  return fetchJson(path`/api/sprites/${uid}/suggest`, { method: 'POST', body })
}
export function publishSprite(uid: string, base_rev: number): Promise<AnnotationEnvelope<CharacterBody>> {
  return fetchJson(path`/api/annotations/${uid}/publish`, { method: 'POST', body: { tier: 'reviewed', base_rev } })
}
export const spriteFrameUrl = (uid: string, clip: string, frame: number) => `${API_BASE}/api/media/${uid}/clips/${encodeURIComponent(clip)}/frame/${frame}.webp?size=512&ordinal=true`
